// <copyright file="CustomerAccountSharingSettings.ext.tsx" company="clarity-ventures.com">
// Copyright (c) clarity-ventures.com. All rights reserved.
// </copyright>

import type { loader } from "@core/admin/admin.accounts.customers.route/admin.accounts.customers_.$id.route";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import { LabeledSwitch } from "@core/components/forms/LabeledSwitch";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { useLoaderData } from "@remix-run/react";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

const sectionTitleKey = "ui.customerAccountSettings.sectionTitle";

function CustomerAccountSharingSettingsContent() {
  const { customer } = useLoaderData<typeof loader>();
  const { control } = useFormContext();
  const { t } = useTranslation();
  const title = t(sectionTitleKey, "Account Settings");

  const { setAccordionValue, accordionValue } = useEditorFrameContext();
  useEffect(() => {
    if (!accordionValue.includes(title)) {
      setAccordionValue((prev) => [...prev, title]);
    }
  }, [accordionValue, setAccordionValue, title]);

  return (
    <EditorSection title={title}>
      <div className="space-y-4">
        <Controller
          name="SharePaymentMethodsAcrossUsers"
          control={control}
          defaultValue={customer.SharePaymentMethodsAcrossUsers ?? true}
          render={({ field }) => (
            <LabeledSwitch
              label={t(
                "ui.customerAccountSettings.sharePaymentMethods",
                "Share saved payment methods across all users on this account",
              )}
              id={`share-pm-${customer.Id}`}
              defaultChecked={field.value}
              onCheckedChange={field.onChange}
              {...field}
            />
          )}
        />
        <Controller
          name="ShareAddressBookAcrossUsers"
          control={control}
          defaultValue={customer.ShareAddressBookAcrossUsers ?? true}
          render={({ field }) => (
            <LabeledSwitch
              label={t(
                "ui.customerAccountSettings.shareAddressBook",
                "Share address book entries across all users on this account",
              )}
              id={`share-ab-${customer.Id}`}
              defaultChecked={field.value}
              onCheckedChange={field.onChange}
              {...field}
            />
          )}
        />
        <Controller
          name="SharePatientsAcrossUsers"
          control={control}
          defaultValue={customer.SharePatientsAcrossUsers ?? true}
          render={({ field }) => (
            <LabeledSwitch
              label={t(
                "ui.customerAccountSettings.sharePatients",
                "Share linked provider patients across all staff users on this account",
              )}
              id={`share-pat-${customer.Id}`}
              defaultChecked={field.value}
              onCheckedChange={field.onChange}
              {...field}
            />
          )}
        />
      </div>
    </EditorSection>
  );
}

registerExtension(
  "admin.accounts.customers_.$id.additionalSections",
  CustomerAccountSharingSettingsContent,
  "CustomerAccountSharingSettings",
);
